import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { precioValido } from "@/componentes/ordenes/lineas"
import { esCuentaBloqueada, PinEnLinea } from "@/componentes/tesoreria/CuentaBloqueada"
import { aCentimos, monto as comoCifra } from "@/componentes/tesoreria/dinero"
import type { Cuenta, Divisa } from "@/componentes/tesoreria/tipos"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { Campo } from "@/paginas/interno/Gastos"

function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/**
 * El proveedor DEVUELVE el saldo disponible de la empresa: el dinero entra a una cuenta.
 *
 * ⚠ **Sólo cuentas de la moneda del saldo.** Un saldo en bolívares devuelto en dólares
 * obligaría a inventar la tasa; el servidor lo rechaza, y aquí ni se ofrece.
 *
 * ⚠ **El soporte es obligatorio**, como en cualquier movimiento a mano: el ingreso sin
 * su captura es la palabra de quien lo teclea contra el extracto del banco.
 *
 * ⚠ **Se propone devolver TODO lo disponible**, que es el caso normal, y se corrige si
 * devolvió una parte: nunca más de lo disponible.
 */
export function DialogoReintegro({
  proveedorId,
  proveedor,
  divisa,
  disponible,
  onCerrar,
}: {
  proveedorId: number
  proveedor: string
  divisa: string
  disponible: string
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [cuentaId, setCuentaId] = useState<number | null>(null)
  const [monto, setMonto] = useState(disponible)
  const [referencia, setReferencia] = useState("")
  const [fecha, setFecha] = useState(hoy())
  const [soporte, setSoporte] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })
  const idDivisa = (divisas.data ?? []).find((d) => d.codigo === divisa)?.id
  const deLaMoneda = (cuentas.data ?? []).filter((c) => c.activo && c.divisa_id === idDivisa)

  const valido = precioValido(monto)
  // En céntimos exactos: nunca en coma flotante.
  const excede = valido !== null && aCentimos(valido) > aCentimos(disponible)
  const listo =
    cuentaId !== null &&
    valido !== null &&
    !excede &&
    referencia.trim().length >= 3 &&
    fecha !== "" &&
    soporte.length === 1

  const registrar = useMutation({
    meta: { exito: "Devolución registrada" },
    mutationFn: () => {
      const datos = new FormData()
      datos.append(
        "datos",
        JSON.stringify({
          cuenta_id: cuentaId,
          monto: valido,
          referencia: referencia.trim(),
          fecha_valor: fecha,
        }),
      )
      if (soporte[0]) datos.append("soporte", soporte[0].archivo)
      return api.post(`/api/v1/saldos-proveedores/${proveedorId}/reintegros`, datos)
    },
    onSuccess: () => {
      for (const clave of [
        ["saldos-proveedores"],
        ["estado-cuenta-proveedor", proveedorId],
        ["cuentas-bancarias"],
        ["proveedor-perfil", proveedorId],
      ])
        clienteQuery.invalidateQueries({ queryKey: clave })
      onCerrar()
    },
  })

  return (
    <Modal
      abierto
      titulo="Devolución de fondos del proveedor"
      descripcion={`${proveedor} · disponible ${comoCifra(disponible)} ${divisa}`}
      onCerrar={onCerrar}
    >
      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (listo) registrar.mutate()
        }}
        onPaste={(evento) => {
          const archivos = archivosPegados(evento.clipboardData, 0)
          if (archivos.length === 0) return
          evento.preventDefault()
          const siguiente = sumar([], archivos.slice(0, 1))
          setSoporte(siguiente.adjuntos)
          setRechazados(siguiente.rechazados)
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo para="reintegro-cuenta" etiqueta={`Cuenta donde entró (${divisa})`}>
            <SelectorBuscable
              id="reintegro-cuenta"
              opciones={deLaMoneda.map((c) => ({
                valor: c.id,
                etiqueta: c.nombre,
                detalle: [c.banco, c.ultimos4 ? `···${c.ultimos4}` : null]
                  .filter(Boolean)
                  .join(" "),
              }))}
              valor={cuentaId}
              onCambiar={setCuentaId}
              placeholder="Buscar cuenta…"
            />
          </Campo>
          <Campo
            para="reintegro-monto"
            etiqueta={`Monto devuelto ${divisa}`}
            ayuda={excede ? "Es más de lo disponible." : undefined}
          >
            <input
              id="reintegro-monto"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              inputMode="decimal"
              className="campo mt-1 tabular-nums"
            />
          </Campo>
          <Campo para="reintegro-referencia" etiqueta="Referencia bancaria">
            <input
              id="reintegro-referencia"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              maxLength={60}
              className="campo mt-1"
            />
          </Campo>
          <Campo para="reintegro-fecha" etiqueta="Fecha valor">
            <input
              id="reintegro-fecha"
              type="date"
              value={fecha}
              max={hoy()}
              onChange={(e) => setFecha(e.target.value)}
              className="campo mt-1"
            />
          </Campo>
        </div>
        {cuentas.data && divisas.data && deLaMoneda.length === 0 && (
          <p className="text-sm" style={{ color: "var(--warning)" }}>
            La agencia no tiene cuentas activas en {divisa}.
          </p>
        )}
        <fieldset>
          <legend className="text-sm font-medium">Comprobante de la devolución</legend>
          <AdjuntosNuevos
            adjuntos={soporte}
            rechazados={rechazados}
            etiqueta="Adjuntar comprobante"
            multiple={false}
            onArchivos={(archivos) => {
              const siguiente = sumar([], archivos.slice(0, 1))
              setSoporte(siguiente.adjuntos)
              setRechazados(siguiente.rechazados)
            }}
            onQuitar={() => setSoporte([])}
          />
        </fieldset>
        <PinEnLinea error={registrar.error} onAbierta={() => registrar.reset()} />
        {registrar.error instanceof ErrorApi && !esCuentaBloqueada(registrar.error) && (
          <p className="text-sm" style={{ color: "var(--destructive)" }}>
            {registrar.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="primario" disabled={!listo || registrar.isPending}>
            {registrar.isPending ? "Registrando…" : "Registrar devolución"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
